/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43fgc52/screen/54184605
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import queryString from 'query-string';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import ToggleColumnMenu from '@components/@core/ToggleColumnMenu';
import AppActions from '~/actions/app';
import ManageActions from '~/actions/manage';
import { CompanyDevelopTabs, ReportMenu } from '~/services/config';
import { exportCsv } from '~/services/util-excel';
import withNavigate from '~/hocs/withNavigate';
import Report from './Report';

const PdfIcon = loadable(() => import('./CompanyTrainingPdf'), { ssr: false });
const moment = extendMoment(originalMoment);

class CompanyTraining extends Component {
    constructor(props) {
        super(props);
        const search = props.location?.search || '';
        this.state = {
            type: queryString.parse(search)?.active || 'overall',
            companyId: props.companyIds,
            userId: -1,
            dateStart: moment().startOf('month').format('YYYY-MM-DD'),
            dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
            page: 1,
            per_page: 25,
            showPrint: false,
        };
    }

    componentDidMount() {
        const { page, per_page, dateStart, dateEnd, companyId } = this.state;
        const payload = {
            company_id: companyId,
            page,
            per_page,
            date_start: dateStart,
            date_end: dateEnd,
        };
        const mode = companyId[0] === -1 ? 'company' : 'individual';
        this.props.getReports(payload, mode);
    }

    handleCompany = (e) => {
        const type = 'individual';
        const { dateStart, dateEnd, page, per_page } = this.state;
        const payload = {
            page,
            per_page,
            date_start: dateStart,
            date_end: dateEnd,
            company_id: [e.id],
        };
        this.props.getReports(payload, type);
        this.setState({ companyId: [e.id] });
    };

    handleDate = (e) => {
        const { companyId, page, per_page } = this.state;
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        const companyIds = companyId[0] === -1 ? this.props.companies.map((item) => item.id) : companyId;
        const type = companyId[0] === -1 ? 'company' : 'individual';
        const payload = {
            page,
            per_page,
            date_start: dateStart,
            date_end: dateEnd,
            company_id: companyIds,
        };
        this.props.getReports(payload, type);
        this.setState({ dateStart, dateEnd });
    };

    handleFilter = (type, e) => {
        const { page, per_page, dateStart, dateEnd, companyId } = this.state;
        const payload = {
            page,
            per_page,
            date_start: dateStart,
            date_end: dateEnd,
        };
        let reportType = type;

        if (type === 'company') {
            if (e[0].id === -1) {
                const companyIds = this.props.companies.map((item) => item.id);
                payload.company_id = companyIds;
            } else {
                const companyIds = e.map((item) => item.id);
                payload.company_id = companyIds;
                reportType = 'individual';
            }
            this.props.getReports(payload, reportType);
            this.setState({ companyId: e.map((item) => item.id) });
        } else {
            reportType = 'individual';
            if (e.length === 0 || e[0].id === -1) {
                payload.company_id = companyId;
                this.setState({ userId: [] });
            } else {
                const userId = e.map((item) => item.id);
                payload.company_id = companyId;
                payload.user_id = userId;
                this.setState({ userId });
            }
            this.props.getReports(payload, reportType);
        }
    };

    handlePagination = (page, per) => {
        const { dateStart, dateEnd, companyId } = this.state;
        let companyIds = [];
        let type = 'company';
        if (Number(companyId) === -1) {
            companyIds = this.props.companies.map((item) => item.id);
        } else {
            companyIds = companyId;
            type = 'individual';
        }
        if (per > 50) {
            page = 1;
        }
        const payload = {
            page,
            per_page: per,
            date_start: dateStart,
            date_end: dateEnd,
            company_id: companyIds,
        };
        this.props.getReports(payload, type);
        this.setState({ page, per_page: per });
    };

    handleTab = (type) => {
        this.props.navigate({ search: `?active=${type.name}` });
        this.setState({ type: type.name });
    };

    handleExcelReport = () => {
        const { companyId } = this.state;
        const { trainingReports } = this.props;
        const mode = companyId[0] === -1 ? 'company' : 'individual';
        const total = trainingReports[mode].total;
        this.handlePagination(this.state.page, total);
        this.props.toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    this.handleExcel();
                },
            },
        });
    };

    handleExcel = () => {
        const { companyId, type } = this.state;
        const { trainingReports } = this.props;
        const mode = companyId[0] === -1 ? 'company' : 'individual';
        const total = trainingReports[mode][type];
        const selected = 'individual' === mode ? trainingReports[mode].individuals : trainingReports[mode].companies;
        let trainingData = [];
        const totalData = {
            Employees: 'Total',
            'Assigned \nCourse': `${total.courses_assigned.count} \n${total.courses_assigned.percent}%`,
            'Incomplete \nCourses': `${total.courses_incompleted.count} \n${total.courses_assigned.percent}%`,
            'Past Due \nCourses': `${total.courses_past_due.count} \n${total.courses_past_due.percent}%`,
            'Completed \nCourses': `${total.courses_completed.count} \n${total.courses_completed.percent}%`,
            'Completed \non time': `${total.courses_completed_on_time.count} \n${total.courses_completed_on_time.percent}%`,
            'Completed \npast due': `${total.courses_completed_past_due.count} \n${total.courses_completed_past_due.percent}%`,
        };
        trainingData.push(totalData);
        selected.map((item) => {
            const { name, report } = item;
            const employeeData = {
                Employees: name,
                'Assigned \nCourse': `${report[type].courses_assigned.count} \n${report[type].courses_assigned.percent}%`,
                'Incomplete \nCourses': `${report[type].courses_incompleted.count} \n${report[type].courses_assigned.percent}%`,
                'Past Due \nCourses': `${report[type].courses_past_due.count} \n${report[type].courses_past_due.percent}%`,
                'Completed \nCourses': `${report[type].courses_completed.count} \n${report[type].courses_completed.percent}%`,
                'Completed \non time': `${report[type].courses_completed_on_time.count} \n${report[type].courses_completed_on_time.percent}%`,
                'Completed \npast due': `${report[type].courses_completed_past_due.count} \n${report[type].courses_completed_past_due.percent}%`,
            };
            trainingData.push(employeeData);
        });
        const key = ['Employees', 'Assigned \nCourse', 'Incomplete \nCourses', 'Past Due \nCourses', 'Completed \nCourses', 'Completed \non time', 'Completed \npast due'];
        exportCsv(trainingData, key, `Report-Training-${type}`, true);
        this.handlePagination(this.state.page, 25);
    };

    handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                this.setState({ showPrint: false });
                this.handleExcelReport();
                break;
            case 'print':
                this.setState({ showPrint: true });
                break;
        }
    };

    render() {
        const { type, dateStart, dateEnd, companyId } = this.state;
        const { userRole, trainingReports } = this.props;
        const date = moment.range(dateStart, dateEnd);
        const mode = companyId[0] === -1 ? 'company' : 'individual';

        return (
            <ErrorBoundary className="company-reports">
                <Filter onChange={this.handleFilter} />
                <div className="card">
                    <div className="d-flex jc-between mb-2 mt-4 mt-lg-0 ai-center">
                        <span className="dsl-b22 bold">Learning Report</span>
                        <div className="d-flex">
                            <DatePicker calendar="range" format="MMM D" as="span" align="right" append="caret" value={date} onSelect={this.handleDate} />
                            <div className="d-h-end cursor-pointer ms-3">
                                <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={this.handleReportMenu} />
                                {this.state.showPrint && <PdfIcon state={this.state} props={this.props} name="Print" />}
                            </div>
                        </div>
                    </div>
                    <ResponsiveTabs data={CompanyDevelopTabs} active={type} getId={(e) => e['name']} getValue={(e) => e['label']} onTab={this.handleTab} />
                    {CompanyDevelopTabs.map(
                        (tab) =>
                            tab.name === type && (
                                <Tab key={type} eventKey={type} title={type === tab.name && tab.label} active={type}>
                                    <Report
                                        total={trainingReports[mode][type]}
                                        data={trainingReports}
                                        userRole={userRole}
                                        type={type}
                                        mode={mode}
                                        companyId={companyId}
                                        navigate={this.props.navigate}
                                        location={this.props.location}
                                        onCompany={this.handleCompany}
                                        onPage={this.handlePagination}
                                    />
                                </Tab>
                            )
                    )}
                </div>
            </ErrorBoundary>
        );
    }
}

CompanyTraining.propTypes = {
    userRole: PropTypes.number.isRequired,
    companies: PropTypes.array.isRequired,
    companyId: PropTypes.number,
    getReports: PropTypes.func.isRequired,
    toggleModal: PropTypes.func.isRequired,
};

CompanyTraining.defaultProps = {
    companyId: 0,
};

const mapStateToProps = (state) => ({
    userRole: state.app.app_role_id,
    companies: state.app.companies,
    employees: state.app.users,
    companyId: state.app.company_info.id,
    trainingReports: state.manage.trainingReports,
    companyIds: state.app.selectedCompany,
});

const mapDispatchToProps = (dispatch) => ({
    getReports: (payload, type) => dispatch(ManageActions.posttrainingreportsRequest(payload, type)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const CompanyTrainingWithNavigate = withNavigate(CompanyTraining);

export default connect(mapStateToProps, mapDispatchToProps)(CompanyTrainingWithNavigate);
